import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home, Bug } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { captureErrorLazy, setSentryErrorBoundaryComponentLazy } from '@/lib/sentry-lazy';
import { logger } from '@/lib/logger';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  level?: 'page' | 'component' | 'critical';
  /**
   * Student-facing wording for this section, replacing the generic title and
   * buttons. `homeAction` adds a way out (e.g. back to the dashboard).
   */
  copy?: ErrorBoundaryCopy;
}

export interface ErrorBoundaryCopy {
  title?: string;
  retryLabel?: string;
  homeAction?: { label: string; href: string };
}

interface State {
  hasError: boolean;
  error?: Error;
  errorId: string;
  timestamp: string;
}

// Utility to sanitize error messages for user display
const sanitizeErrorMessage = (error: Error): string => {
  const message = error.message?.toLowerCase() || '';
  
  // Don't expose sensitive error details
  if (message.includes('api key') || 
      message.includes('token') || 
      message.includes('secret') ||
      message.includes('password') ||
      message.includes('database') ||
      message.includes('internal server')) {
    return 'An internal error occurred. Please try again or contact support.';
  }
  
  // Network errors
  if (message.includes('network') || 
      message.includes('fetch') || 
      message.includes('connection')) {
    return 'Connection error. Please check your internet connection and try again.';
  }
  
  // Authentication errors
  if (message.includes('unauthorized') || 
      message.includes('authentication') ||
      message.includes('forbidden')) {
    return 'Authentication error. Please refresh the page and try again.';
  }
  
  // Validation errors (these are usually safe to show)
  if (message.includes('validation') || 
      message.includes('required') ||
      message.includes('invalid')) {
    return error.message;
  }
  
  // Generic fallback
  return 'Something went wrong. Please try again.';
};

// Generate a unique error ID for tracking
const generateErrorId = (): string => {
  return `err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Log error securely (without exposing sensitive data in console)
const logErrorSecurely = (error: Error, errorInfo: ErrorInfo, errorId: string, level: string) => {
  const errorReport = {
    errorId,
    level,
    timestamp: new Date().toISOString(),
    message: error.message,
    stack: error.stack,
    componentStack: errorInfo.componentStack,
    userAgent: navigator.userAgent,
    url: window.location.href,
  };
  
  // In development, log full details
  if (import.meta.env.DEV) {
    logger.error('Error boundary caught error in development', {
      context: 'ErrorBoundary',
      action: 'developmentError',
      metadata: {
        errorId,
        level,
        error: error.message,
        stack: error.stack,
        userAgent: navigator.userAgent,
        url: window.location.href
      }
    });
  } else {
    // In production, log minimal info and send to monitoring service
    logger.error('Error boundary caught exception', {
      context: 'ErrorBoundary',
      action: 'productionError',
      metadata: {
        errorId,
        message: sanitizeErrorMessage(error)
      }
    });

    // Error tracking handled by Sentry integration
    // Sentry automatically captures and reports errors with proper sanitization
  }
};

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      errorId: '',
      timestamp: '',
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
      errorId: generateErrorId(),
      timestamp: new Date().toISOString(),
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { level = 'component', onError } = this.props;
    
    // Set Sentry context for this error boundary
    setSentryErrorBoundaryComponentLazy(`ErrorBoundary-${level}`);
    
    // Send error to Sentry with enhanced context
    captureErrorLazy(error, {
      component: `ErrorBoundary-${level}`,
      feature: 'error_boundary',
      extra: {
        errorId: this.state.errorId,
        componentStack: errorInfo.componentStack,
        level,
        timestamp: this.state.timestamp,
      },
    });
    
    // Log error securely
    logErrorSecurely(error, errorInfo, this.state.errorId, level);
    
    // Log to Sentry structured logging
    logger.error('Error boundary caught error', {
      errorId: this.state.errorId,
      level,
      message: error.message,
      componentStack: errorInfo.componentStack,
    });
    
    // Call custom error handler if provided
    if (onError) {
      onError(error, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: undefined,
      errorId: '',
      timestamp: '',
    });
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleReportBug = () => {
    const { errorId, timestamp, error } = this.state;
    const sanitizedMessage = error ? sanitizeErrorMessage(error) : 'Unknown error';
    
    // Create a support email or redirect to bug report form
    const subject = encodeURIComponent(`Bug Report - Error ${errorId}`);
    const body = encodeURIComponent(
      `Error ID: ${errorId}\n` +
      `Timestamp: ${timestamp}\n` +
      `Message: ${sanitizedMessage}\n` +
      `URL: ${window.location.href}\n\n` +
      `Please describe what you were doing when this error occurred:\n`
    );
    
    window.open(`mailto:admin@coachsim.co.uk?subject=${subject}&body=${body}`);
  };

  render() {
    const { hasError, error, errorId, timestamp } = this.state;
    const { children, fallback, level = 'component', copy } = this.props;

    if (hasError) {
      // Use custom fallback if provided
      if (fallback) {
        return fallback;
      }

      const sanitizedMessage = error ? sanitizeErrorMessage(error) : 'An unexpected error occurred';
      const isPageLevel = level === 'page';
      const isCritical = level === 'critical';

      return (
        <div className={`flex items-center justify-center ${isPageLevel ? 'min-h-screen p-4' : 'p-4'}`}>
          <Card className={`w-full max-w-md ${isCritical ? 'border-red-500' : 'border-orange-500'}`}>
            <CardHeader className="text-center">
              <div className={`mx-auto mb-4 ${isCritical ? 'text-red-500' : 'text-orange-500'}`}>
                <AlertTriangle size={48} />
              </div>
              <CardTitle className="text-lg">
                {copy?.title ?? (isCritical ? 'Critical Error' : 'Something went wrong')}
              </CardTitle>
              <Badge variant="outline" className="mx-auto mt-2">
                Error ID: {errorId}
              </Badge>
            </CardHeader>
            
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground text-center">
                {sanitizedMessage}
              </p>
              
              <div className="text-xs text-muted-foreground text-center">
                Time: {new Date(timestamp).toLocaleString()}
              </div>
              
              <div className="flex flex-col gap-2">
                <Button 
                  onClick={this.handleRetry}
                  variant="default"
                  className="w-full"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  {copy?.retryLabel ?? 'Try Again'}
                </Button>

                {copy?.homeAction && (
                  <Button
                    onClick={() => { window.location.href = copy.homeAction!.href; }}
                    variant="outline"
                    className="w-full"
                  >
                    <Home className="w-4 h-4 mr-2" />
                    {copy.homeAction.label}
                  </Button>
                )}

                {isPageLevel && !copy?.homeAction && (
                  <Button 
                    onClick={this.handleGoHome}
                    variant="outline"
                    className="w-full"
                  >
                    <Home className="w-4 h-4 mr-2" />
                    Go Home
                  </Button>
                )}
                
                <Button 
                  onClick={this.handleReportBug}
                  variant="ghost"
                  size="sm"
                  className="w-full"
                >
                  <Bug className="w-4 h-4 mr-2" />
                  Report Bug
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return children;
  }
}

// Higher-order component for easy wrapping
export const withErrorBoundary = <P extends object>(
  Component: React.ComponentType<P>,
  errorBoundaryProps?: Omit<Props, 'children'>
) => {
  const WrappedComponent = (props: P) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  );
  
  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;
  return WrappedComponent;
};

// Hook for programmatic error handling
export const useErrorHandler = () => {
  const handleError = React.useCallback((error: Error, context?: string) => {
    const errorId = generateErrorId();
    const sanitizedMessage = sanitizeErrorMessage(error);
    
    // Log the error
  if (import.meta.env.DEV) {
      logger.error('Error handler caught error in development', {
        context: 'useErrorHandler',
        action: 'developmentError',
        metadata: {
          errorContext: context || 'Unknown',
          errorId,
          error: error.message
        }
      });
    }

    // You could also throw the error to be caught by an error boundary
    // or dispatch to a global error state
    logger.error('Application error handled', {
      context: 'useErrorHandler',
      action: 'applicationError',
      metadata: {
        errorContext: context || 'application',
        message: sanitizedMessage
      }
    });
    
    return { errorId, sanitizedMessage };
  }, []);
  
  return { handleError };
};
